/*
 * File: api-constants.tsx
 * Project: a-a
 * File Created: Tuesday, 15th October 2024 8:15:39 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Monday, 17th November 2025 11:39:43 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */

import { ClientActivityQueryParams, ClientTaskQueryParams } from '../model/client.model';
import { ActivityQueryParams, TaskQueryParams } from '../model/dashboard.model';
import { MemberTaskQueryParams } from '../model/team.model';

export class ApiConstants {
  public static readonly AUTH_API = {
    VALIDATE: {
      URL: (user: string) => `validate_user?userName=${user}`,
    },
    CREATE_USER: {
      URL: () => `create_user`,
    },
    RESEND_CONFIRMATION: {
      URL: () => `resend_confirmation`,
    },
    SET_PASSWORD: {
      URL: () => `set_password`,
    },
    FORGOT_PASSWORD: {
      URL: () => `forgot_password`,
    },
    CHANGE_PASSWORD: {
      URL: () => `/change_password`,
    },

    LOGIN: {
      URL: () => `login`,
    },
    LOGOUT: {
      URL: () => `logout`,
    },
    REFRESH: {
      URL: () => `/refresh_token`,
    },
    GET_HUDDLE_INFO: {
      URL: () => `/get_huddle_info`,
    },
    MS_LOGIN: {
      URL: (id: string, uri: string) => `/ms_login_tokens?code=${id}&redirect_uri=${uri}`,
    },
    MS_TEAM_LOGIN: {
      URL: () => `/ms_auth/login?redirect_uri=${process.env.REACT_APP_URI}/ms_auth/redirect`,
    },
    MS_TEAM_EVENTS: {
      URL: (code: string, email: string, isExistingAccessTokenValid: boolean) =>
        `/team_meetings?email=${email}&redirect_uri=${process.env.REACT_APP_REDIRECT}${
          code && '&code=' + code
        }${isExistingAccessTokenValid && '&isExistingAccessTokenValid=true'}`,
    },
    GOOGLE_AUTHCALLBACK: {
      URL: (code: string, url: string) =>
        `/google-calendar/oauth2callback?code=${code}&redirect_uri=${url}`,
    },
    ZOOM_AUTHCALLBACK: {
      URL: (code: string, url: string) => `/zoom/authCallback?code=${code}&redirect_uri=${url}`,
    },
    GOOGLE_REFRESH: {
      URL: () => `/google-calendar/refresh-token`,
    },
  };
  public static readonly CALENDAR_API = {
    GOOGLE_CALENDAR: {
      URL: () => `/google-calendar/events?`,
      QUERY_PARAMS: (queryParams?: any): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    ZOOM_CALENDAR: {
      URL: () => `/zoom/meetings`,
    },
  };
  public static readonly DASHBOARD_API = {
    GET_DEAL: {
      URL: () => `sf/get_rollup_nums`,
    },
    GET_TASKS: {
      URL: () => `sf/get_tasks?`,
      QUERY_PARAMS: (queryParams?: TaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    GET_ACTIVITY: {
      URL: () => `sf/recent_activity?`,
      QUERY_PARAMS: (queryParams?: ActivityQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    GET_CLIENT: {
      URL: () => `sf/client_filter_values`,
    },
    GET_ACTION_ACCOUNT: {
      URL: () => `sf/actionable_accounts`,
    },
    GET_SPORTS_NEWS: {
      URL: () => `sports-news`,
    },
  };
  public static readonly CLIENT_API = {
    ALL_CLIENT: {
      URL: () => `clients/all_clients`,
    },
    ADD_DEAL: {
      URL: () => `/clients/deal`,
    },
    EDIT_DEAL: {
      URL: (id: string) => `/clients/deal/${id}`,
    },
    ALL_PRICEBOOKS: {
      URL: () => `/clients/pricebooks`,
    },
    CLIENT_DETAILS: {
      URL: (id: string) => `clients/client_profile_details?accountId=${id}`,
    },
    DEAL_TYPES: {
      URL: () => `/clients/deal_types`,
    },
    DEAL_DROPDOWN: { URL: (id?: string) => `/clients/deal_dropdowns${id ? '?id=' + id : ''}` },
    CREATE_PRICEBOOK_PRODUCTS: {
      URL: () => `/clients/opplineitems`,
    },
    EDIT_PRICEBOOK_PRODUCTS: {
      URL: (id: string) => `/clients/opplineitems/${id}`,
    },
    DELETE_DEAL: {
      URL: (id: string) => `clients/deal/${id}`,
    },
    PRICEBOOK_PRODUCTS: {
      URL: (id: string) => `/clients/pricebook/${id}/products`,
    },
    CLIENT_TASKS: {
      URL: (id: string) => `clients/client_tasks?accountId=${id}&`,
      QUERY_PARAMS: (queryParams?: ClientTaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    CLIENT_DROPDOWNS: {
      URL: (id: string) => `/clients/client_dropdowns?clientType=${id}`,
    },
    CLIENT_ADD: {
      URL: () => `/clients/client`,
    },
    CLIENT_ACTIVITY: {
      URL: (id: string) => `clients/client_activity?accountId=${id}&`,
      QUERY_PARAMS: (queryParams?: ClientActivityQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    CLIENT_CONTACTS: {
      URL: (id: string) => `clients/client_contacts?accountId=${id}`,
    },
    CLIENT_DEAL: {
      URL: (id: string) => `clients/client_rollup_nums?accountId=${id}`,
    },
    CLIENT_KEY_DEALS: {
      URL: (id: string) => `clients/key_deals?accountId=${id}&`,
      QUERY_PARAMS: (queryParams?: MemberTaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    DELETE_EDIT_CLIENT: {
      URL: (id: string) => `/clients/client/${id}`,
    },
  };
  public static readonly HUDDLE_API = {
    SAVE_PROFILE: {
      URL: (email: string) => `/sf/user/profile/${email}`,
    },
  };
  public static readonly TEAM_API = {
    ALL_MEMBER: {
      URL: () => `team/members`,
    },
    MEMBER_DEALS: {
      URL: (id: string) => `team/member/deals?memberId=${id}`,
    },
    MEMBER_TASKS: {
      URL: (id: string) => `team/tasks?memberId=${id}&`,
      QUERY_PARAMS: (queryParams?: MemberTaskQueryParams): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
  };
  public static readonly SF_API = {
    GET_DROPDOWN: {
      URL: (email: string) => `/sf/task_dropdowns?mailId=${email}`,
    },
    GET_SF_TOKEN: {
      URL: (code: string) => `/sf/get_tokens?code=${code}`,
    },
    SF_TASK: {
      URL: () => `/sf/task`,
    },
    EDIT_SF_TASK: {
      URL: (code: string) => `/sf/task/${code}`,
    },
    DELETE_SF_TASK: {
      URL: (code: string) => `/sf/task/${code}`,
    },
    GET_PRESIGNED_URL: {
      URL: (mail: string, type: string, key: string) =>
        `/sf/get_presigned_url?mailId=${mail}&contentType=${type}&key=${key}`,
    },
    S3_UPLOAD: {
      URL: () => process.env.REACT_APP_S3 || '',
    },
    GET_PROFILE_DETAILS: {
      URL: (email: string) => `sf/user/profile/${email}`,
    },
  };
  public static readonly NOTES_API = {
    ALL_NOTES: {
      URL: (id: string) => `/sf/notes/${id}?`,
      QUERY_PARAMS: (queryParams?: { access_token: string }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value]) => `${key}=${value}`).join('&');
      },
    },
    ADD_EDIT_NOTES: {
      URL: () => `/sf/notes`,
    },
    DELETE_NOTES: {},
  };
  public static readonly CAPTIVE8_API = {
    MAIN_INFO: { URL: (id: string) => `/captiv8/info/${id}` },
    CREATOR: { URL: (id: string) => `/captiv8/creator/performance/${id}` },
    SEARCH_ID: { URL: (user: string) => `/captiv8/search-users?query=${user}` },
  };
  public static readonly ASANA_API = {
    ASANA_LOGIN: {
      URL: () => `/asana/exchange-token`,
    },
    ASANA_LOGOUT: {
      URL: () => `/asana/auth/logout`,
    },
    ASANA_VALIDATE_TOKEN: {
      URL: () => `/asana/auth/validate-token`,
    },
    ASANA_REFRESH: {
      URL: () => `/asana/refresh-token`,
    },

    STATISTICS: {
      URL: () => `/asana/stats`,
    },
    TEAMS: {
      URL: () => `/asana/teams`,
    },
    PROJECTS: {
      URL: () => `/asana/projects?`,
      QUERY_PARAMS: (queryParams?: {
        team: string;
        member: string;
        owner: string;
        portfolio: string;
      }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value], ind) => `${key}=${value}`).join('&');
      },
    },
    FILTER_OPTIONS: {
      URL: () => `/asana/filter-options`,
    },
    CREATE_PROJECT: {
      URL: () => `/asana/create-project`,
    },
    EDIT_PROJECT: {
      URL: (id: string) => `/asana/projects/${id}`,
    },
    DELETE_PROJECT: {
      URL: (id: string) => `/asana/projects/${id}`,
    },
    TASKS_BY_SECTION: {
      URL: (id: string) => `/asana/projects/${id}/tasks?`,
      QUERY_PARAMS: (queryParams?: {
        priority: string;
        status: string;
        assignee: string;
        created_by: string;
        overdue: string;
      }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value], ind) => `${key}=${value}`).join('&');
      },
    },
    MOVE_BY_SECTION: {
      URL: (id: string) => `/asana/sections/${id}/move-task`,
    },
    TASK_FILTER: {
      URL: (id: string) => `/asana/projects/${id}/task-filter-options`,
    },
    CREATE_TASKS: {
      URL: (id: string) => `/asana/projects/${id}/tasks`,
    },
    EDIT_DELETE_TASKS: {
      URL: (id: string) => `/asana/tasks/${id}`,
    },
    ASANA_TASK_ATTACHMENTS: {
      URL: (id: string) => `/asana/tasks/${id}/attachments`,
    },
    ASANA_TASK_BY_ID: {
      URL: (id: string) => `/asana/tasks/${id}`,
    },
    ASANA_ATTACHMENTS_REMOVE: {
      URL: (id: string) => `/asana/attachments/${id}`,
    },
    ASANA_INVITE_MEMBERS: {
      URL: (id: string) => `/asana/projects/${id}/invite`,
    },
    ASANA_ADD_SECTION: {
      URL: (id: string) => `/asana/projects/${id}/sections`,
    },
    ASANA_EDIT_DELETE_SECTION: {
      URL: (id: string) => `/asana/sections/${id}`,
    },
  };
  public static readonly SLACK_API = {
    SLACK_LOGIN: {
      URL: (code: string) => `/slack/authCallback?code=${code}`,
    },

    SLACK_CHANNEL_MESSAGE: {
      URL: (id: string, next?: string) =>
        `/slack/channels/${id}/messages?limit=10${next ? `&cursor=${next}` : ''}`,
    },

    SLACK_UNLINK: {
      URL: () => `/unlink_huddle_info`,
    },
    SLACK_CHANNELS: {
      URL: () => `/slack/channels/`,
    },
  };
  public static readonly LOCATION = {
    GOOGLE_LOCATION: {
      URL: (loc: string) => `/clients/client_address_suggestions?location=${loc}`,
    },
  };
  public static readonly ZOOMINFO = {
    TOP_UPDATES: {
      URL: (page = 1) => `/zoominfo/top-updates?page=${page}&pageSize=5`,
    },
    SEARCH_ZOOMINFO: {
      URL: (query: string) => `/zoominfo/search?q=${query}`,
    },
    TARGET_ACCOUNTS: {
      URL: (page = 1) => `/zoominfo/targets?page=${page}&pageSize=5`,
    },
    SUGGESTION: {
      URL: (page = 1) => `/zoominfo/suggestions?limit=5&page=${page}`,
    },
    ADD_TARGET: {
      URL: () => `/zoominfo/targets`,
    },
    COMPANY_DETAILS: {
      URL: (companyId: string) => `/zoominfo/company/${companyId}`,
    },
    COMPANY_EMPLOYEE: {
      URL: (companyId: string, page: string) =>
        `/zoominfo/company/${companyId}/employees?page=${page}&pageSize=10`,
      QUERY_PARAMS: (queryParams?: { department: string }): string => {
        const validParams = Object.entries(queryParams || {}).filter(
          ([_, value]) => value != null && value !== '',
        );

        // Map the valid params into query string format and join them with '&'
        return validParams.map(([key, value], ind) => `&${key}=${value}`).join('&');
      },
    },
    COMPANY_NEWS: {
      URL: (companyId: string, page = 1) =>
        `/zoominfo/company/${companyId}/news?page=${page}&pageSize=10`,
    },
    COMPANY_SIMILAR: {
      URL: (companyId: string, page = 1) =>
        `/zoominfo/company/${companyId}/similar?page=${page}&pageSize=10`,
    },
    COMPANY_SIGNALS: {
      URL: (companyId: string, page = 1) =>
        `/zoominfo/company/${companyId}/signals?days=30&page=${page}&pageSize=20`,
    },
    ADD_TAG: {
      URL: (id: string) => `/zoominfo/company/${id}/tags`,
    },
    DELETE_TAG: {
      URL: (id: string, tag: string) => `/zoominfo/company/${id}/tags/${tag}`,
    },
    GET_TAGS: {
      URL: () => `/zoominfo/tags`,
    },
    ADD_DELETE_LISTS: {
      URL: (id: string) => `/zoominfo/company/${id}/lists`,
    },
    EXPORT_COMPANY_DATA: {
      URL: (id: string) => `/zoominfo/company/${id}/export`,
    },
    DELETE_TARGET: {
      URL: (id: string) => `/zoominfo/targets/${id}`,
    },
    EXPORT_CONTACT: {
      URL: (id: string) => `/zoominfo/company/${id}/contacts/export`,
    },
    EMPLOYEE_DROPDOWN: {
      URL: (id: string) => `/zoominfo/company/${id}/employees/filters`,
    },
    DISMISS_FEED: {
      URL: (id: string) => `/zoominfo/company/${id}/block`,
    },
    CORPORATE_HIERCHY: {
      URL: (id: string) => `https://app.zoominfo.com/#/apps/profile/company/${id}/locations`,
    },
  };
  public static readonly FILES_API = {
    GET_PRESIGNED: {
      URL: (clientId: string, params: URLSearchParams) =>
        `/clients/${clientId}/files/presigned-url?${params}`,
    },
    POST_FILES: {
      URL: (clientId: string) => `/clients/${clientId}/files`,
    },
    GET_FILES: {
      URL: (clientId: string) => `/clients/${clientId}/files`,
    },
    GET_FILE_DETAILS: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}`,
    },
    DOWNLOAD_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}/download`,
    },
    SHARE_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}/share`,
      QUERY_PARAMS: (queryParams?: { expiresIn: number }): string => {
        const validParams = Object.entries(queryParams || {}).filter(([_, value]) => value != null);

        return validParams.map(([key, value]) => `&${key}=${value}`).join('&');
      },
    },
    DELETE_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}`,
    },
    RENAME_FILE: {
      URL: (clientId: string, fileId: string) => `/clients/${clientId}/files/${fileId}/rename`,
    },
  };
}
